import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Button, IconButton, MenuItem, Stack, TextField } from '@mui/material';

import type { FeeTier, FeeType } from '../../api/fees.api.js';

type FeeKind = 'FLAT' | 'PERCENTAGE' | 'TIER';

export function formatCents(cents: string, currency: string): string {
  try {
    const value = Number(BigInt(cents)) / 100;
    return new Intl.NumberFormat('en-EU', {
      style: 'currency',
      currency,
    }).format(value);
  } catch {
    return `${cents} ${currency}`;
  }
}

export function describeFeeType(feeType: FeeType): string {
  switch (feeType.kind) {
    case 'FLAT':
      return `Flat ${feeType.amountCents} cents`;
    case 'PERCENTAGE':
      return `${(feeType.basisPoints / 100).toFixed(2)}%`;
    case 'TIER':
      return `${feeType.tiers.length} tier(s)`;
  }
}

interface FeeTypeBuilderProps {
  value: FeeType;
  onChange: (next: FeeType) => void;
}

export function FeeTypeBuilder({ value, onChange }: FeeTypeBuilderProps): JSX.Element {
  const kind = value.kind;

  const changeKind = (next: FeeKind): void => {
    if (next === 'FLAT') onChange({ kind: 'FLAT', amountCents: '0' });
    else if (next === 'PERCENTAGE') onChange({ kind: 'PERCENTAGE', basisPoints: 0 });
    else onChange({ kind: 'TIER', tiers: [] });
  };

  const updateTier = (index: number, next: FeeTier): void => {
    if (value.kind !== 'TIER') return;
    const tiers = value.tiers.map((t, i) => (i === index ? next : t));
    onChange({ kind: 'TIER', tiers });
  };

  const addTier = (): void => {
    if (value.kind !== 'TIER') return;
    onChange({
      kind: 'TIER',
      tiers: [
        ...value.tiers,
        {
          minAmountCents: '0',
          fee: { kind: 'FLAT', amountCents: '0' },
        },
      ],
    });
  };

  const removeTier = (index: number): void => {
    if (value.kind !== 'TIER') return;
    onChange({
      kind: 'TIER',
      tiers: value.tiers.filter((_, i) => i !== index),
    });
  };

  return (
    <Stack spacing={2}>
      <TextField
        select
        label="Fee type"
        value={kind}
        onChange={(e) => changeKind(e.target.value as FeeKind)}
      >
        <MenuItem value="FLAT">Flat</MenuItem>
        <MenuItem value="PERCENTAGE">Percentage</MenuItem>
        <MenuItem value="TIER">Tiered</MenuItem>
      </TextField>

      {value.kind === 'FLAT' && (
        <TextField
          label="Amount (cents)"
          type="number"
          value={value.amountCents}
          onChange={(e) =>
            onChange({ kind: 'FLAT', amountCents: e.target.value || '0' })
          }
        />
      )}

      {value.kind === 'PERCENTAGE' && (
        <TextField
          label="Basis points (100 = 1%)"
          type="number"
          inputProps={{ min: 0, max: 10_000 }}
          value={value.basisPoints}
          onChange={(e) =>
            onChange({ kind: 'PERCENTAGE', basisPoints: Number(e.target.value) || 0 })
          }
        />
      )}

      {value.kind === 'TIER' && (
        <Box>
          <Stack spacing={1}>
            {value.tiers.map((tier, i) => (
              <Stack key={i} direction="row" spacing={1} alignItems="center">
                <TextField
                  label="Min cents"
                  type="number"
                  size="small"
                  value={tier.minAmountCents}
                  onChange={(e) =>
                    updateTier(i, { ...tier, minAmountCents: e.target.value || '0' })
                  }
                />
                <TextField
                  label="Max cents"
                  type="number"
                  size="small"
                  value={tier.maxAmountCents ?? ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    const next: FeeTier = { ...tier };
                    if (v === '') {
                      delete next.maxAmountCents;
                    } else {
                      next.maxAmountCents = v;
                    }
                    updateTier(i, next);
                  }}
                />
                <TextField
                  label="Flat fee (cents)"
                  type="number"
                  size="small"
                  value={tier.fee.kind === 'FLAT' ? tier.fee.amountCents : '0'}
                  onChange={(e) =>
                    updateTier(i, {
                      ...tier,
                      fee: { kind: 'FLAT', amountCents: e.target.value || '0' },
                    })
                  }
                />
                <IconButton size="small" onClick={() => removeTier(i)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
          </Stack>
          <Button startIcon={<AddIcon />} onClick={addTier} sx={{ mt: 1 }}>
            Add tier
          </Button>
        </Box>
      )}
    </Stack>
  );
}
